import { useRoute } from '#imports'
import { computed } from 'vue'

import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { useLocalStorage } from '@backmarket/utils/composables/useLocalStorage'

import translations from './usePriceChangeToast.translations'

const STORAGE_KEY_PREFIX = 'CHECKOUT_PRICE_CHANGE_TOASTS_'
const TRACKING_ZONE = 'price_change_toast'

export type PriceChangeSource = 'cart_price_change' | 'auto_replacement'

export type PriceChange = {
  id: string
  previousAmount: string
  currentAmount: string
  source: PriceChangeSource
  modelName: string
  sellerChanged?: boolean
}

function getTrackingValue(
  isPriceChanged: boolean,
  sellerChanged: boolean,
  isPriceDecrease: boolean,
): string {
  if (!isPriceChanged) {
    return 'seller_change'
  }

  if (sellerChanged) {
    return isPriceDecrease
      ? 'price_decrease_seller_change'
      : 'price_increase_seller_change'
  }

  return isPriceDecrease ? 'price_decrease' : 'price_increase'
}

export function usePriceChangeToast() {
  const i18n = useI18n()
  const { openSuccessToast, openNeutralToast } = useTheToast()
  const localStorage = useLocalStorage()
  const userStore = useUserStore()
  const { trackClick, trackToastView } = useTracking()
  const route = useRoute()

  const storageKey = computed(
    () =>
      `${STORAGE_KEY_PREFIX}${userStore.user.value.userId ?? 'unauthenticated'}`,
  )

  function getNotifiedAmounts(): Record<string, string> {
    return JSON.parse(localStorage.getItem(storageKey.value) ?? '{}')
  }

  function trackDismiss() {
    trackClick({
      name: 'dismissed',
      zone: TRACKING_ZONE,
      pageType: route.name as string,
    })
  }

  function notifyPriceChange({
    id,
    previousAmount,
    currentAmount,
    source,
    modelName,
    sellerChanged = false,
  }: PriceChange) {
    const isPriceChanged = previousAmount !== currentAmount

    if (!isPriceChanged && !sellerChanged) {
      return
    }

    const notifiedAmounts = getNotifiedAmounts()
    if (notifiedAmounts[id] === currentAmount) {
      return
    }

    const isPriceDecrease = Number(currentAmount) < Number(previousAmount)
    const trackingValue = getTrackingValue(
      isPriceChanged,
      sellerChanged,
      isPriceDecrease,
    )

    trackToastView({
      name: 'displayed',
      zone: TRACKING_ZONE,
      pageType: route.name as string,
      source,
      value: trackingValue,
    })
    localStorage.setItem(
      storageKey.value,
      JSON.stringify({ ...notifiedAmounts, [id]: currentAmount }),
    )

    if (sellerChanged) {
      if (!isPriceChanged) {
        openNeutralToast({
          name: id,
          title: i18n(translations.sellerChangeTitle),
          content: i18n(translations.sellerChangeDescription, { modelName }),
          closeCallback: trackDismiss,
        })
      } else if (isPriceDecrease) {
        openSuccessToast({
          name: id,
          title: i18n(translations.priceSellerChangeDownTitle),
          content: i18n(translations.priceSellerChangeDownDescription, {
            modelName,
          }),
          closeCallback: trackDismiss,
        })
      } else {
        openNeutralToast({
          name: id,
          title: i18n(translations.priceSellerChangeUpTitle),
          content: i18n(translations.priceSellerChangeUpDescription, {
            modelName,
          }),
          closeCallback: trackDismiss,
        })
      }
    } else if (isPriceDecrease) {
      openSuccessToast({
        name: id,
        title: i18n(translations.priceDownTitle),
        content: i18n(translations.priceDownDescription, { modelName }),
        closeCallback: trackDismiss,
      })
    } else {
      openNeutralToast({
        name: id,
        title: i18n(translations.priceUpTitle),
        content: i18n(translations.priceUpDescription, { modelName }),
        closeCallback: trackDismiss,
      })
    }
  }

  return {
    notifyPriceChange,
  }
}
